// Service for "Zero Knowledge" client-side encryption
// This service handles key generation, derivation, wrapping, and unwrapping.

export interface EncryptionParams {
  salt: string;
  wrappedKey: string;
  iv: string; // Initialization Vector used for wrapping the key
}

// Configuration constants
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 256;
const DIGEST_ALGORITHM = 'SHA-256';

/**
 * Utility: Convert ArrayBuffer (or view) to Base64 string
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer | ArrayBufferView): string => {
  let binary = '';
  // Ensure we are working with bytes
  let bytes: Uint8Array;
  if (buffer instanceof Uint8Array) {
    bytes = buffer;
  } else if (ArrayBuffer.isView(buffer)) {
    bytes = new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  } else {
    bytes = new Uint8Array(buffer);
  }

  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    // biome-ignore lint/style/noNonNullAssertion: Loop is within bounds
    binary += String.fromCharCode(bytes[i]!);
  }
  return window.btoa(binary);
};

/**
 * Utility: Convert Base64 string to ArrayBuffer
 */
export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Generates a random salt
 */
export const generateSalt = (): Uint8Array => {
  return window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
};

/**
 * Generates a random Initialization Vector (IV)
 */
export const generateIV = (): Uint8Array => {
  return window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
};

/**
 * Generates a random recovery code (high entropy)
 * Format: 4 groups of 4 alphanumeric characters (e.g. A1B2-C3D4-E5F6-G7H8)
 */
export const generateRecoveryCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Base32-like alphabet to avoid confusion
  const length = 16;
  const values = new Uint8Array(length);
  window.crypto.getRandomValues(values);

  let code = '';
  for (let i = 0; i < length; i++) {
    // biome-ignore lint/style/noNonNullAssertion: Loop is within bounds
    const charIndex = values[i]! % chars.length;
    code += chars[charIndex];
    if ((i + 1) % 4 === 0 && i !== length - 1) {
      code += '-';
    }
  }
  return code;
};

/**
 * Generates a random Master Data Key (MDK) - AES-GCM 256
 */
export const generateMasterKey = async (): Promise<CryptoKey> => {
  return window.crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: KEY_LENGTH,
    },
    true, // extractable (must be true to wrap it)
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
};

/**
 * Derives a Key Encryption Key (KEK) from a password/code and salt using PBKDF2
 */
export const deriveKey = async (secret: string, salt: Uint8Array): Promise<CryptoKey> => {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: DIGEST_ALGORITHM,
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false, // KEK is not extractable
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
};

/**
 * Wraps (encrypts) the Master Data Key (MDK) using the Key Encryption Key (KEK)
 */
export const wrapKey = async (
  mdk: CryptoKey,
  kek: CryptoKey,
  iv: Uint8Array
): Promise<ArrayBuffer> => {
  return window.crypto.subtle.wrapKey('raw', mdk, kek, {
    name: 'AES-GCM',
    iv: iv as unknown as BufferSource,
  });
};

/**
 * Unwraps (decrypts) the Wrapped Key to restore the Master Data Key (MDK)
 */
export const unwrapKey = async (
  wrappedKey: BufferSource,
  kek: CryptoKey,
  iv: Uint8Array
): Promise<CryptoKey> => {
  return window.crypto.subtle.unwrapKey(
    'raw',
    wrappedKey,
    kek,
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
    },
    {
      name: 'AES-GCM',
      length: KEY_LENGTH,
    },
    true, // MDK must be extractable so we can use it
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
};

/**
 * Full flow: Setup Identity Keys (RSA) for a user to allow sharing
 * Returns the Public Key (JWK) and Encrypted Private Key (Base64)
 */
export const setupIdentityKeys = async (
  mdk: CryptoKey
): Promise<{ publicKey: JsonWebKey; encryptedPrivateKey: string }> => {
  // 1. Generate RSA Key Pair
  const keyPair = await generateKeyPair();

  // 2. Export Public Key (Safe to share)
  const publicKey = await exportPublicKey(keyPair.publicKey);

  // 3. Export Private Key (Must be encrypted)
  const privateKeyJWK = await exportPrivateKey(keyPair.privateKey);
  const privateKeyString = JSON.stringify(privateKeyJWK);
  const privateKeyBuffer = new TextEncoder().encode(privateKeyString);

  // 4. Encrypt Private Key with MDK
  const encryptedBuffer = await encryptData(mdk, privateKeyBuffer.buffer);
  const encryptedPrivateKey = arrayBufferToBase64(encryptedBuffer);

  return {
    publicKey,
    encryptedPrivateKey,
  };
};

/**
 * Full flow: Setup encryption for a new user
 * Returns the params to send to the server (password-wrapped & recovery-wrapped)
 * and the MDK/RecoveryCode for immediate use/display.
 */
export const setupEncryption = async (
  password: string
): Promise<{
  params: EncryptionParams;
  recoveryParams: EncryptionParams;
  mdk: CryptoKey;
  recoveryCode: string;
}> => {
  // 1. Generate MDK
  const mdk = await generateMasterKey();

  // 2. Generate Recovery Code
  const recoveryCode = generateRecoveryCode();

  // 3. Prepare Salts & IVs
  const salt = generateSalt();
  const iv = generateIV();
  const recoverySalt = generateSalt();
  const recoveryIv = generateIV();

  // 4. Derive KEKs
  const kek = await deriveKey(password, salt);
  const recoveryKek = await deriveKey(recoveryCode, recoverySalt);

  // 5. Wrap MDK with both KEKs
  const wrappedKeyBuffer = await wrapKey(mdk, kek, iv);
  const recoveryWrappedKeyBuffer = await wrapKey(mdk, recoveryKek, recoveryIv);

  return {
    params: {
      salt: arrayBufferToBase64(salt),
      wrappedKey: arrayBufferToBase64(wrappedKeyBuffer),
      iv: arrayBufferToBase64(iv),
    },
    recoveryParams: {
      salt: arrayBufferToBase64(recoverySalt),
      wrappedKey: arrayBufferToBase64(recoveryWrappedKeyBuffer),
      iv: arrayBufferToBase64(recoveryIv),
    },
    mdk,
    recoveryCode,
  };
};

/**
 * Full flow: Restore Master Key from storage parameters and password/code
 */
export const restoreMasterKey = async (
  secret: string,
  params: EncryptionParams
): Promise<CryptoKey> => {
  const salt = new Uint8Array(base64ToArrayBuffer(params.salt));
  const iv = new Uint8Array(base64ToArrayBuffer(params.iv));
  const wrappedKeyBuffer = base64ToArrayBuffer(params.wrappedKey);

  // 1. Derive KEK
  const kek = await deriveKey(secret, salt);

  // 2. Unwrap MDK
  return unwrapKey(new Uint8Array(wrappedKeyBuffer), kek, iv);
};

/**
 * Exports the Master Key as a raw base64 string.
 * This allows sending the key to the server for transient processing ("Key Forwarding").
 * SECURITY WARNING: Only send this over secure (HTTPS) channels to trusted backend services.
 */
export const exportKey = async (key: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(exported);
};

// Alias for restoration from recovery code (same logic, just semantic difference)
export const restoreMasterKeyFromRecovery = restoreMasterKey;

/**
 * Imports a raw base64 Master Key string back into a CryptoKey.
 * Used for restoring the key from session storage.
 */
export const importMasterKey = async (base64Key: string): Promise<CryptoKey> => {
  const rawKey = base64ToArrayBuffer(base64Key);
  return window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    true, // extractable
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
};

/**
 * Encrypts data (ArrayBuffer) using the Master Key.
 * Prepends the 12-byte IV to the ciphertext.
 * Output format: [IV (12 bytes)] + [Ciphertext]
 */
export const encryptData = async (key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer> => {
  const iv = generateIV();
  const ciphertext = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
    },
    key,
    data
  );

  // Combine IV and Ciphertext
  const result = new Uint8Array(iv.byteLength + ciphertext.byteLength);
  result.set(new Uint8Array(iv), 0);
  result.set(new Uint8Array(ciphertext), iv.byteLength);

  return result.buffer;
};

/**
 * Decrypts data (ArrayBuffer) using the Master Key.
 * Expects the first 12 bytes to be the IV.
 */
export const decryptData = async (key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer> => {
  const input = new Uint8Array(data);

  if (input.byteLength < IV_LENGTH) {
    throw new Error('Invalid data: too short to contain IV');
  }

  const iv = input.slice(0, IV_LENGTH);
  const ciphertext = input.slice(IV_LENGTH);

  return window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
    },
    key,
    ciphertext
  );
};

// ==========================================
// RSA Asymmetric Encryption (For Sharing)
// ==========================================

/**
 * Generates an RSA-OAEP Key Pair (Public/Private)
 * Used for "User Identity Keys" to allow secure sharing.
 */
export const generateKeyPair = async (): Promise<CryptoKeyPair> => {
  return window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true, // extractable
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
};

/**
 * Exports a Public Key to JWK format (safe to store openly)
 */
export const exportPublicKey = async (key: CryptoKey): Promise<JsonWebKey> => {
  return window.crypto.subtle.exportKey('jwk', key);
};

/**
 * Imports a Public Key from JWK format
 */
export const importPublicKey = async (jwk: JsonWebKey): Promise<CryptoKey> => {
  return window.crypto.subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'wrapKey']
  );
};

/**
 * Exports a Private Key to JWK format (MUST be encrypted before storage)
 */
export const exportPrivateKey = async (key: CryptoKey): Promise<JsonWebKey> => {
  return window.crypto.subtle.exportKey('jwk', key);
};

/**
 * Imports a Private Key from JWK format
 */
export const importPrivateKey = async (jwk: JsonWebKey): Promise<CryptoKey> => {
  return window.crypto.subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['decrypt', 'unwrapKey']
  );
};

/**
 * Encrypts data (e.g., a TEK) using a Public Key
 */
export const encryptWithPublicKey = async (
  publicKey: CryptoKey,
  data: ArrayBuffer
): Promise<ArrayBuffer> => {
  return window.crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey,
    data
  );
};

/**
 * Decrypts data using a Private Key
 */
export const decryptWithPrivateKey = async (
  privateKey: CryptoKey,
  data: ArrayBuffer
): Promise<ArrayBuffer> => {
  return window.crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
    },
    privateKey,
    data
  );
};
